import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "How JAMStack helps you ship",
  "description": "Common problem in engineering teams: Stepping on each other's toes. JAMStack can help.",
  "published": "2020-10-27T00:00:00.000Z",
  "hero": "./img/screenshot-1603812044466.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`Yesterday we talked about a common problem in engineering teams: Stepping on each other's toes.`}</p>
    <p>{`You want to ship a feature but Alice's bugfix isn't ready and your code is mixed. Now you're waiting ...`}</p>
    <p>{`One solution is trunk-based development with feature flags. Everyone works off master and hides their code behind a gazillion if statements.`}</p>
    <p>{`You'll need a strict unit and integration testing discipline, a system for feature flags, engineers who never break things, strong typing, good documentation, and the Beyonce rule: `}<em parentName="p">{`If you liked it you shoulda put a test on it.`}</em></p>
    <p>{`Anyone might change anything at any time.`}</p>
    <p><video parentName="p" {...{
        "style": {
          "margin": "auto auto",
          "display": "block",
          "maxWidth": "80%"
        },
        "autoPlay": true,
        "loop": true,
        "muted": true,
        "playsInline": true,
        "loading": "lazy"
      }}>{`
            `}<source parentName="video" {...{
          "src": "https://media2.giphy.com/media/d1E1msx7Yw5Ne1Fe/giphy-loop.mp4?cid=7cf28b0f6lhgvcm2eevrfd34tj3j1jfcra9ryf3e8ny372qa&rid=giphy-loop.mp4&ct=g",
          "type": "video/mp4"
        }}></source>{`
        `}</video></p>
    <p>{`Trunk-based development works for Google, Facebook, Apple, and other giant enterprises.`}</p>
    <p>{`You and me ... eh I dunno. I've seen mixed results and crying users.`}</p>
    <h2 {...{
      "id": "jamstack-is-like-frontend-microservices"
    }}>{`JAMStack is like frontend microservices`}</h2>
    <p>{`Another solution are microservices.`}</p>
    <p>{`You split your app into areas of interest, known as concerns. Each becomes its own independent piece of code. Ship when it needs to, run its own environment, have its own versions, use its own libraries.`}</p>
    <p>{`This worked for Amazon. It's why AWS has so many products. Others followed suit.`}</p>
    <p>{`With this approach teams can iterate independently. Shared code goes into versioned libraries, specific code goes into versioned services.`}</p>
    <p>{`Alice never has to wait for you and you never wait for her.`}</p>
    <p><video parentName="p" {...{
        "style": {
          "margin": "auto auto",
          "display": "block",
          "maxWidth": "80%"
        },
        "autoPlay": true,
        "loop": true,
        "muted": true,
        "playsInline": true,
        "loading": "lazy"
      }}>{`
            `}<source parentName="video" {...{
          "src": "https://media0.giphy.com/media/3oEjI5VtIhHvK37WYo/giphy-loop.mp4?cid=7cf28b0fkeu4vuqzlf1erpd8so9umwas4gutcrm2cd73desh&rid=giphy-loop.mp4&ct=g",
          "type": "video/mp4"
        }}></source>{`
        `}</video></p>
    <p>{`JAMStack brings this to the frontend and it's amazing my friend.`}</p>
    <p>{`It stands for Javascript-Apis-and-Markdown.`}</p>
    <p>{`You build the frontend in Javascript, connect to APIs, and use Markdown for content-heavy portions. Easier to write that way trust me :)`}</p>
    <h2 {...{
      "id": "shift-your-mindset-to-the-jam"
    }}>{`Shift your mindset to the JAM`}</h2>
    <p>{`Your first separation of concerns comes from splitting the frontend and backend. You've seen this with SinglePageApps.`}</p>
    <p>{`React, Vue, Angular, etc. apps are built this way. Render your UI in the browser, call the server through an API.`}</p>
    <p>{`Backend and frontend can iterate independently. Great.`}</p>
    <p>{`You run into issues as your frontend grows.`}</p>
    <p>{`Should engineers build the marketing page? Landing pages for specific ad campaigns? Should they help with every tiny tweak and marketing A/B experiment? Even when there's 50 per week?`}</p>
    <p>{`Don't have enough work building the product? 😉`}</p>
    <p>{`There's your first split: Product vs. marketing parts of the frontend.`}</p>
    <p>{`Yep, they want to look the same and share styling. Yes marketing wants a lot of the same API data as the product. Oh yes, the signup widget needs to create users and there's a Login button.`}</p>
    <p>{`But marketing and product fundamentally evolve at a different pace.`}</p>
    <p>{`Another fun one are admin interfaces and internal tools. Needs all the same data, moves much slower. Ain't nobody got time to polish those.`}</p>
    <p><video parentName="p" {...{
        "style": {
          "margin": "auto auto",
          "display": "block",
          "maxWidth": "80%"
        },
        "autoPlay": true,
        "loop": true,
        "muted": true,
        "playsInline": true,
        "loading": "lazy"
      }}>{`
            `}<source parentName="video" {...{
          "src": "https://media3.giphy.com/media/LXBQpXZrclqlPEGZ7G/giphy-loop.mp4?cid=7cf28b0facbf0xs42g77e9lzocrf3783zvf44lcl836t1y5v&rid=giphy-loop.mp4&ct=g",
          "type": "video/mp4"
        }}></source>{`
        `}</video></p>
    <h2 {...{
      "id": "how-the-jamstack-helps"
    }}>{`How the JAMStack helps`}</h2>
    <p>{`Here's what you do to solve those problems: Split your app.`}</p>
    <p>{`You build a core library for shared features. Start with the design system. You'll need that everywhere and it's easy to split off. A library that gives you perfect-looking UI off a napkin sketch.`}</p>
    <p>{`😍`}</p>
    <p>{`Then you create separate JAMStack apps per area. One for marketing, one for admin, one for each internal tool, one or two for the core product ... it depends!`}</p>
    <p>{`Every app uses the same core stack:`}</p>
    <p>{`- your design system`}</p>
    <ul>
      <li parentName="ul">{`your shared libraries`}</li>
      <li parentName="ul">{`your APIs`}</li>
    </ul>
    <p>{`When admin creates a user it calls the same API as the SignUp button on the marketing homepage.`}</p>
    <p><video parentName="p" {...{
        "style": {
          "margin": "auto auto",
          "display": "block",
          "maxWidth": "80%"
        },
        "autoPlay": true,
        "loop": true,
        "muted": true,
        "playsInline": true,
        "loading": "lazy"
      }}>{`
            `}<source parentName="video" {...{
          "src": "https://media1.giphy.com/media/WXZ5DqIOhQrxtkcszN/giphy-loop.mp4?cid=7cf28b0fu1qbos2al9qt0nz0zsev8qoooyl4jnf1yy1hg2pg&rid=giphy-loop.mp4&ct=g",
          "type": "video/mp4"
        }}></source>{`
        `}</video></p>
    <p>{`And despite that, every app is independent:`}</p>
    <ul>
      <li parentName="ul">{`its own deploy frequency`}</li>
      <li parentName="ul">{`its own versions`}</li>
      <li parentName="ul">{`its own deploy environment`}</li>
      <li parentName="ul">{`its own staging and preview`}</li>
    </ul>
    <p>{`Wait what?`}</p>
    <p>{`Yep. They're independent apps. You can work on each area of your overall product separately. Alice doesn't care what Bob is changing. Neither worries about `}<em parentName="p">{`your`}</em>{` changes.`}</p>
    <p>{`It's great.`}</p>
    <p>{`Plus modern tooling gives you a fantastic preview and deploy experience. Every pull request becomes its own live URL. Share it with designers and PMs and they see where you're at.`}</p>
    <p>{`Feedback early and often. Don't even have to wait for someone to approve your pull request. Show it off before merge 😍`}</p>
    <p>{`When's a better time to hear `}<em parentName="p">{`"That button doesn't work, make it blue and change the text"`}</em>{`: 2 weeks after pull request got merged, or 5 minutes after you typed the code?`}</p>
    <p>{`I know my answer.`}</p>
    <p>{`Cheers,`}<br />{`
~Swizec`}</p>
    <p>{`PS: tomorrow I'm sharing something new, stay tuned`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      